import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Chargement, EnTetePage, Etiquette, Message } from "@cabinet/charte";
import { administrationApi, dossiersApi, ErreurApi } from "../api/client";
import { formaterDate } from "../domaine/formats";
import { useSession } from "../session";

const message = (echec: unknown) => (echec instanceof ErreurApi ? echec.message : "L'opération n'a pas abouti.");

/**
 * Utilisateurs du tenant et leurs habilitations (EF-0104).
 *
 * Une habilitation compose un rôle et une portée : le tenant entier, ou un seul
 * dossier. Une personne déjà connue de la plateforme garde son compte et reçoit
 * un droit de plus ; une personne inconnue reçoit un compte, avec un mot de
 * passe initial conforme à la politique. Un retrait vaut sur-le-champ.
 */
export function PageUtilisateurs() {
  const { session } = useSession();
  const cache = useQueryClient();
  const utilisateurs = useQuery({ queryKey: ["administration-utilisateurs"], queryFn: administrationApi.utilisateurs });
  const roles = useQuery({ queryKey: ["administration-roles"], queryFn: administrationApi.roles });
  const dossiers = useQuery({ queryKey: ["dossiers"], queryFn: dossiersApi.lister });
  const [saisie, setSaisie] = useState({ courriel: "", nom: "", motDePasse: "", role: "", dossier: "" });
  const [retour, setRetour] = useState<{ type: "succes" | "erreur"; texte: string } | null>(null);

  // L'administration de la plateforme ne s'accorde pas d'ici ; le rôle
  // proposé d'office est celui du collaborateur, le plus courant.
  const rolesProposes = (roles.data ?? []).filter((r) => !r.permissions.includes("ADMINISTRER_PLATEFORME"));
  const roleChoisi = saisie.role || (rolesProposes.find((r) => r.code === "COLLABORATEUR") ?? rolesProposes[0])?.id || "";

  const rafraichir = () => void cache.invalidateQueries({ queryKey: ["administration-utilisateurs"] });
  const habilitation = useMutation({
    mutationFn: () =>
      administrationApi.habiliter({
        courriel: saisie.courriel,
        nom: saisie.nom,
        motDePasse: saisie.motDePasse || null,
        role: roleChoisi,
        dossier: saisie.dossier || null,
        debut: null,
        fin: null,
      }),
    onSuccess: () => {
      rafraichir();
      setRetour({ type: "succes", texte: `${saisie.courriel} habilité.` });
      setSaisie({ courriel: "", nom: "", motDePasse: "", role: saisie.role, dossier: "" });
    },
    onError: (echec) => setRetour({ type: "erreur", texte: message(echec) }),
  });
  const action = useMutation({
    mutationFn: (faire: () => Promise<unknown>) => faire(),
    onSuccess: rafraichir,
    onError: (echec) => setRetour({ type: "erreur", texte: message(echec) }),
  });

  const nomDuDossier = (id: string | null) =>
    id == null ? "Tout le tenant" : (dossiers.data?.find((d) => d.id === id)?.raisonSociale ?? "Dossier");

  return (
    <>
      <EnTetePage
        surTitre={session?.tenant.raisonSociale}
        titre="Utilisateurs"
        description="Chaque droit compose un rôle et une portée — tout le tenant, ou un seul dossier. Un retrait vaut sur-le-champ : les droits sont relus à chaque requête."
      />

      {retour != null && <Message type={retour.type}>{retour.texte}</Message>}

      <form
        className="carte"
        onSubmit={(e) => {
          e.preventDefault();
          setRetour(null);
          habilitation.mutate();
        }}
      >
        <h3>Habiliter une personne</h3>
        <div className="champs-en-ligne">
          <label className="champ">
            Courriel
            <input type="email" required value={saisie.courriel} onChange={(e) => setSaisie({ ...saisie, courriel: e.target.value })} />
          </label>
          <label className="champ">
            Nom
            <input required value={saisie.nom} onChange={(e) => setSaisie({ ...saisie, nom: e.target.value })} />
          </label>
          <label className="champ">
            Mot de passe initial
            <input
              type="password"
              autoComplete="new-password"
              value={saisie.motDePasse}
              placeholder="si nouveau compte"
              onChange={(e) => setSaisie({ ...saisie, motDePasse: e.target.value })}
            />
          </label>
          <label className="champ">
            Rôle
            <select value={roleChoisi} onChange={(e) => setSaisie({ ...saisie, role: e.target.value })}>
              {rolesProposes.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.libelle}
                </option>
              ))}
            </select>
          </label>
          <label className="champ">
            Portée
            <select value={saisie.dossier} onChange={(e) => setSaisie({ ...saisie, dossier: e.target.value })}>
              <option value="">Tout le tenant</option>
              {(dossiers.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.raisonSociale}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="principal" disabled={habilitation.isPending || roleChoisi === ""}>
            Habiliter
          </button>
        </div>
        <p className="discret-texte">
          Mot de passe initial : douze caractères au moins, mêlant trois familles — minuscules, majuscules, chiffres,
          symboles. Inutile si la personne a déjà un compte sur la plateforme.
        </p>
      </form>

      {utilisateurs.isPending && <Chargement quoi="les utilisateurs" />}
      {utilisateurs.error != null && <Message type="erreur">{message(utilisateurs.error)}</Message>}

      {utilisateurs.data != null && (
        <div className="tableau-conteneur">
          <table>
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Second facteur</th>
                <th>Droits</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {utilisateurs.data.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.nom}
                    <div className="discret-texte">{u.courriel}</div>
                  </td>
                  <td>
                    {u.secondFacteurActif ? (
                      <Etiquette variante="succes">Actif</Etiquette>
                    ) : (
                      <Etiquette variante="neutre">Inactif</Etiquette>
                    )}
                  </td>
                  <td>
                    {u.habilitations.map((h) => (
                      <div key={h.id} className="habilitation">
                        <Etiquette variante={h.active ? "info" : "neutre"}>{h.roleLibelle}</Etiquette>{" "}
                        <span className="discret-texte">
                          {nomDuDossier(h.dossier)} · depuis le {formaterDate(h.debut)}
                          {h.fin != null && ` jusqu'au ${formaterDate(h.fin)}`}
                        </span>{" "}
                        {h.active && (
                          <button
                            type="button"
                            className="tertiaire"
                            onClick={() =>
                              window.confirm(`Retirer à ${u.nom} le rôle ${h.roleLibelle} ?`) &&
                              action.mutate(() => administrationApi.retirer(h.id))
                            }
                          >
                            Retirer
                          </button>
                        )}
                      </div>
                    ))}
                  </td>
                  <td>
                    {u.secondFacteurActif && (
                      <button
                        type="button"
                        className="tertiaire"
                        onClick={() =>
                          window.confirm(`Réinitialiser le second facteur de ${u.nom} ? Il devra l'enrôler à nouveau.`) &&
                          action.mutate(() => administrationApi.reinitialiserLeSecondFacteur(u.id))
                        }
                      >
                        Réinitialiser le second facteur
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
